import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  TrainerDTO,
  ClubTrainerPublicDTO,
  UserSearchResultDTO,
  CreateTrainerRequest,
  PromoteTrainerRequest,
  InviteTrainerRequest,
  UpdateTrainerProfileRequest,
  GetClubTrainersPublicResponse,
  GetMyTrainerProfileResponse,
} from "@membentry/shared-types";
import { TrainerInviteConflictError } from "@membentry/shared-types";
import type { AxiosInstance } from "axios";

// ─── Query Keys ─────────────────────────────────────────────────

export const TRAINERS_QUERY_KEY = "trainers";
export const CLUB_USER_SEARCH_QUERY_KEY = "club-user-search";
export const CLUB_TRAINERS_PUBLIC_QUERY_KEY = "club-trainers-public";
export const MY_TRAINER_PROFILE_QUERY_KEY = "my-trainer-profile";

export const getTrainersQueryKey = (clubId: number, params?: { page?: number; pageSize?: number; search?: string }) =>
  [TRAINERS_QUERY_KEY, clubId, params] as const;

export const getClubUserSearchQueryKey = (clubId: number, query: string) =>
  [CLUB_USER_SEARCH_QUERY_KEY, clubId, query] as const;

export const getClubTrainersPublicQueryKey = (clubId: number) =>
  ["clubs", clubId, "trainers", "public"] as const;

export const getMyTrainerProfileQueryKey = (clubId: number) =>
  ["clubs", clubId, "trainers", "me"] as const;

// ─── Trainer Query Hooks ────────────────────────────────────────

export function useGetTrainersQuery(
  client: AxiosInstance,
  clubId: number,
  params?: { page?: number; pageSize?: number; search?: string },
  options?: Omit<UseQueryOptions<TrainerDTO[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: getTrainersQueryKey(clubId, params),
    queryFn: async () => {
      const { getTrainers } = await import("@membentry/shared-api");
      const response = await getTrainers(client, clubId, params);
      return response.payload ?? [];
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useSearchClubUsersQuery(
  client: AxiosInstance,
  clubId: number,
  query: string,
  options?: Omit<UseQueryOptions<UserSearchResultDTO[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: getClubUserSearchQueryKey(clubId, query),
    queryFn: async () => {
      const { searchClubUsers } = await import("@membentry/shared-api");
      return searchClubUsers(client, clubId, query);
    },
    enabled: !!clubId && query.trim().length >= 2,
    ...options,
  });
}

export function useClubTrainersPublicQuery(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<UseQueryOptions<ClubTrainerPublicDTO[], Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: getClubTrainersPublicQueryKey(clubId),
    queryFn: async () => {
      const { getClubTrainersPublic } = await import("@membentry/shared-api");
      const response = await getClubTrainersPublic(client, clubId);
      return response.payload ?? [];
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useMyTrainerProfileQuery(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<UseQueryOptions<ClubTrainerPublicDTO | null, Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: getMyTrainerProfileQueryKey(clubId),
    queryFn: async () => {
      const { getMyTrainerProfile } = await import("@membentry/shared-api");
      const response = await getMyTrainerProfile(client, clubId);
      return response.payload?.trainer ?? null;
    },
    enabled: !!clubId,
    ...options,
  });
}

// ─── Trainer Mutation Hooks ─────────────────────────────────────

export function usePromoteTrainerMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<UseMutationOptions<TrainerDTO, Error, PromoteTrainerRequest>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: PromoteTrainerRequest) => {
      const { promoteTrainer } = await import("@membentry/shared-api");
      return promoteTrainer(client, clubId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINERS_QUERY_KEY, clubId] });
    },
    ...options,
  });
}

export function useInviteTrainerMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<UseMutationOptions<TrainerDTO, TrainerInviteConflictError | Error, InviteTrainerRequest>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: InviteTrainerRequest) => {
      const { inviteTrainer } = await import("@membentry/shared-api");
      return inviteTrainer(client, clubId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINERS_QUERY_KEY, clubId] });
    },
    ...options,
  });
}

export function useDeleteTrainerMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (trainerId: number) => {
      const { deleteTrainer } = await import("@membentry/shared-api");
      return deleteTrainer(client, clubId, trainerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINERS_QUERY_KEY, clubId] });
    },
    ...options,
  });
}

export function useUpdateTrainerProfileMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<
      ClubTrainerPublicDTO,
      Error,
      { trainerId: number; body: UpdateTrainerProfileRequest }
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      trainerId,
      body,
    }: {
      trainerId: number;
      body: UpdateTrainerProfileRequest;
    }) => {
      const { updateTrainerProfile } = await import("@membentry/shared-api");
      const response = await updateTrainerProfile(client, clubId, trainerId, body);
      return response.payload!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getClubTrainersPublicQueryKey(clubId) });
      queryClient.invalidateQueries({ queryKey: getMyTrainerProfileQueryKey(clubId) });
      queryClient.invalidateQueries({ queryKey: [TRAINERS_QUERY_KEY, clubId] });
    },
    ...options,
  });
}

/**
 * @deprecated Use `useInviteTrainerMutation` or `usePromoteTrainerMutation` instead.
 * This hook is kept for backward compatibility with legacy code.
 */
export function useCreateTrainerMutation(
  client: AxiosInstance,
  options?: Omit<UseMutationOptions<TrainerDTO, Error, CreateTrainerRequest>, "mutationFn">,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTrainerRequest) => {
      const { createTrainer } = await import("@membentry/shared-api");
      const response = await createTrainer(client, data);
      return response.payload!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINERS_QUERY_KEY] });
    },
    ...options,
  });
}
